// LearnCore Frontend - 认证上下文

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../api/client';
import type { User, RegisterRequest, LoginRequest } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 初始化时检查用户状态
  useEffect(() => {
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      console.log('[AuthContext] 检查当前用户状态...');
      setLoading(true);
      const response = await apiClient.getCurrentUser();
      console.log('[AuthContext] 获取到用户信息:', response.user);
      setUser(response.user);
    } catch (err: any) {
      // 如果是401错误，说明用户未登录，这是正常情况
      if (err.response?.status !== 401) {
        console.error('[AuthContext] 检查用户状态失败:', err);
      } else {
        console.log('[AuthContext] 用户未登录 (401)');
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (data: LoginRequest) => {
    try {
      console.log('[AuthContext] 开始登录流程，数据:', { ...data, password: '[HIDDEN]' });
      setLoading(true);
      setError(null);
      
      const response = await apiClient.login(data);
      console.log('[AuthContext] 登录API响应:', response);
      
      setUser(response.user);
      console.log('[AuthContext] 用户信息已设置:', response.user);
    } catch (err: any) {
      console.error('[AuthContext] 登录失败:', err);
      const message = err.response?.data?.error || '登录失败';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.register(data);
      setUser(response.user);
    } catch (err: any) {
      const message = err.response?.data?.error || '注册失败';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiClient.logout();
    } catch (err) {
      console.error('登出失败:', err);
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
